import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { pickProjectTherapeuticArea } from '../utils/therapeuticArea';
import { useSession } from './SessionContext';

const ProjectContext = createContext();

/**
 * Project Context Provider
 * Accepts initialProject from server (CheckSession). On project change, calls api.post('/Auth/SetProject').
 * When project changes, server returns recordedByMappingId; we update session audit so nRecordedBy is correct.
 */
export const ProjectProvider = ({ children, defaultProject = 'PROJ-001', initialProject = null }) => {
  const { updateAuditValue } = useSession();
  const initialCode = initialProject?.projectCode || initialProject?.ProjectCode || defaultProject;
  const [currentProject, setCurrentProject] = useState(initialCode);
  const [projectInfo, setProjectInfo] = useState(initialProject || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialProject && (initialProject.projectCode || initialProject.ProjectCode)) {
      const code = initialProject.projectCode || initialProject.ProjectCode;
      setCurrentProject(code);
      setProjectInfo(initialProject);
    }
  }, [initialProject]);

  // SetProject is called only once when session is bound (in App.js after CheckSession). Here we only run when user explicitly changes project.
  const updateProject = async (projectCode) => {
    if (!projectCode || projectCode === 'all') return;
    setCurrentProject(projectCode);
    try {
      setLoading(true);
      const response = await api.get('/ImportData/GetProject');
      const projects = response.data || [];
      const project = projects.find(
        p => (p.projectCode || p.ProjectCode) === projectCode
      );
      if (project) {
        const projectData = {
          // Phase 4 cutover: ProjectNo is canonical; keep projectId only for backward compatibility.
          projectId: project.projectId ?? project.ProjectId ?? project.nProjectId ?? project.projectNo ?? project.ProjectNo,
          projectNo: project.projectNo || project.ProjectNo,
          projectCode: project.projectCode || project.ProjectCode,
          projectDescription: project.projectDescription || project.ProjectDescription || '',
          vTherapueticArea: pickProjectTherapeuticArea(project),
          drugName: project.drugName || project.DrugName || '',
          globalProjectCode: project.globalProjectCode || project.GlobalProjectCode || '',
          isActive: project.isActive !== undefined ? project.isActive : project.IsActive,
        };
        setProjectInfo(projectData);
        const res = await api.post('/Auth/SetProject', projectData);
        if (res.data?.recordedByMappingId != null) {
          updateAuditValue('nRecordedBy', res.data.recordedByMappingId);
        }
        window.dispatchEvent(new Event('projectUpdated'));
      } else {
        console.warn(`Project with code ${projectCode} not found in database`);
      }
    } catch (err) {
      console.error('Error fetching project info:', err);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentProject,
    projectInfo,
    updateProject,
    setCurrentProject: updateProject,
    loading,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return context;
};

export const withProject = (Component) => {
  return (props) => (
    <ProjectProvider>
      <Component {...props} />
    </ProjectProvider>
  );
};
